import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import { companyInfo } from '../../utils/dataInfo';

// Helper function to get supplier name
const getSupplierName = (supplierId, supplierNamesData) => {
  if (!supplierNamesData || !Array.isArray(supplierNamesData)) return 'Unknown';
  const supplier = supplierNamesData.find(supplier => parseInt(supplier.id) === parseInt(supplierId));
  return supplier ? supplier.name : 'Unknown';
};

// Helper function to get stock name
const getStockName = (stockId, stockNamesData) => {
  if (!stockNamesData || !Array.isArray(stockNamesData)) return 'Unknown';
  const stock = stockNamesData.find(stock => parseInt(stock.id) === parseInt(stockId));
  return stock ? stock.name : 'Unknown';
};

// Helper function to get branch name
const getBranchName = (branchId, branchNamesData) => {
  if (!branchNamesData || !Array.isArray(branchNamesData)) return 'Unknown';
  const branch = branchNamesData.find(branch => parseInt(branch.id) === parseInt(branchId));
  return branch ? `${branch.name}` : 'Unknown';
};

// Helper function to format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US').format(amount) + " "+"sh";
};

// Helper function to split text into multiple lines
const splitTextIntoLines = (doc, text, maxWidth) => {
  const words = text.split(' ');
  const lines = [];
  let currentLine = words[0];

  for (let i = 1; i < words.length; i++) {
    const word = words[i];
    const width = doc.getTextWidth(currentLine + ' ' + word);
    if (width < maxWidth) {
      currentLine += ' ' + word;
    } else {
      lines.push(currentLine);
      currentLine = word;
    }
  }
  lines.push(currentLine);
  return lines;
};

// Manual table drawing function for PDF - UPDATED FOR INVENTORY
const drawInventoryTable = (doc, data, startY, referenceData) => {
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const margin = 20;
  const rowHeight = 12;

  // Fixed column widths for landscape mode - CORRECTED
  const colWidths = [20, 40, 50, 60, 30, 40, 40, 50]; // #, Date, Supplier, Stock Item, Quantity, Cost, Selling, Branch

  let currentY = startY;
  const neededHeight = (data.length + 1) * rowHeight;

  // Check if we need a new page
  if (currentY + neededHeight > pageHeight - 30) {
    doc.addPage('l'); // Add landscape page
    currentY = margin;
  }

  // Draw table header
  doc.setFillColor(25, 118, 210); // Blue header
  doc.setTextColor(255, 255, 255); // White text
  doc.setFont(undefined, 'bold');

  const headers = ['#', 'Stock Date', 'Supplier', 'Stock Item', 'Quantity', 'Cost', 'Selling', 'Branch'];
  let xPos = margin;

  // Draw header background
  doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');

  // Draw header text
  headers.forEach((header, index) => {
    const cellX = xPos + 2;
    doc.text(header, cellX, currentY + 7);
    xPos += colWidths[index];
  });

  currentY += rowHeight;

  // Draw table rows
  doc.setFont(undefined, 'normal');
  doc.setTextColor(0, 0, 0);

  data.forEach((row, rowIndex) => {
    // Calculate needed height for this row
    let maxLines = 1;
    const cellLines = row.map((cell, cellIndex) => {
      const text = String(cell);
      const maxWidth = colWidths[cellIndex] - 4;
      const lines = splitTextIntoLines(doc, text, maxWidth);
      maxLines = Math.max(maxLines, lines.length);
      return lines;
    });

    const actualRowHeight = rowHeight * maxLines;

    // Check for page break
    if (currentY + actualRowHeight > pageHeight - 30) {
      doc.addPage('l'); // Add landscape page
      currentY = margin;
      
      // Redraw header on new page
      doc.setFillColor(25, 118, 210);
      doc.setTextColor(255, 255, 255);
      doc.setFont(undefined, 'bold');
      
      doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
      
      xPos = margin;
      headers.forEach((header, index) => {
        doc.text(header, xPos + 2, currentY + 7);
        xPos += colWidths[index];
      });
      
      currentY += rowHeight;
      doc.setFont(undefined, 'normal');
      doc.setTextColor(0, 0, 0);
    }
    
    // Alternate row colors
    if (rowIndex % 2 === 0) {
      doc.setFillColor(248, 248, 248);
    } else {
      doc.setFillColor(255, 255, 255);
    }
    
    // Draw row background
    doc.rect(margin, currentY, pageWidth - (2 * margin), actualRowHeight, 'F');
    
    // Draw cell borders
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.1);
    
    // Draw vertical lines
    let verticalX = margin;
    for (let i = 0; i <= colWidths.length; i++) {
      doc.line(verticalX, currentY, verticalX, currentY + actualRowHeight);
      if (i < colWidths.length) verticalX += colWidths[i];
    }
    
    // Draw horizontal lines
    doc.line(margin, currentY, pageWidth - margin, currentY);
    doc.line(margin, currentY + actualRowHeight, pageWidth - margin, currentY + actualRowHeight);
    
    // Draw row text with multi-line support
    cellLines.forEach((lines, cellIndex) => {
      xPos = margin + colWidths.slice(0, cellIndex).reduce((sum, width) => sum + width, 0);
      
      lines.forEach((line, lineIndex) => {
        const lineY = currentY + 7 + (lineIndex * rowHeight);
        doc.text(line, xPos + 2, lineY);
      });
    });
    
    currentY += actualRowHeight;
  });

  return currentY;
};

// Excel export for Restaurant Inventory
export const exportToExcel = (inventory, referenceData = {}, filters = {}) => {
  try {
    const { supplierNamesData = [], stockNamesData = [], branchNamesFilterData = [] } = referenceData;

    // Prepare data for Excel with ALL attributes including Branch
    const excelData = inventory.map((item, index) => ({
      'No.': index + 1,
      'ID': item.id,
      'Stock Date': new Date(item.stock_date).toLocaleDateString(),
      'Supplier': getSupplierName(item.supplier_id, supplierNamesData),
      'Stock Item': getStockName(item.stock_id, stockNamesData),
      'Quantity': item.quantity,
      'Cost Price': formatCurrency(item.cost),
      'Selling Price': formatCurrency(item.selling),
      'Total Cost': formatCurrency(item.quantity * item.cost),
      'Total Selling': formatCurrency(item.quantity * item.selling),
      'Profit Margin': formatCurrency((item.selling - item.cost) * item.quantity),
      'Description': item.description || '',
      'Branch': getBranchName(item.branch_id, branchNamesFilterData),
      'Created Date': new Date(item.created_at).toLocaleDateString(),
      'Updated Date': new Date(item.updated_at).toLocaleDateString()
    }));

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Auto-size columns
    const maxWidth = excelData.reduce((widths, row) => {
      Object.keys(row).forEach((key, idx) => {
        const length = String(row[key]).length;
        if (!widths[idx] || length > widths[idx]) {
          widths[idx] = length;
        }
      });
      return widths;
    }, []);
    
    worksheet['!cols'] = maxWidth.map(width => ({ width: Math.min(width + 2, 50) }));

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Restaurant Inventory');

    // Generate Excel file and download
    const fileName = `restaurant_inventory_export_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    return { success: true, fileName };
  } catch (error) {
    console.error('Restaurant Inventory Excel export failed:', error);
    throw new Error('Failed to export to Excel: ' + error.message);
  }
};

// PDF export for Restaurant Inventory
export const exportToPDF = (inventory, referenceData = {}, filters = {}) => {
  return new Promise((resolve, reject) => {
    try {
      const { supplierNamesData = [], stockNamesData = [], branchNamesFilterData = [] } = referenceData;
      
      // Create PDF in landscape mode
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });
      
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      let currentY = margin;
      
      // Title
      doc.setFontSize(20);
      doc.setTextColor(25, 118, 210);
      doc.text("RESTAURANT INVENTORY REPORT", pageWidth / 2, currentY, { align: 'center' });
      currentY += 15;
      
      // Company information with better layout to prevent email overlap
      doc.setFontSize(9);
      doc.setTextColor(80, 80, 80);
      
      // Company name and address on separate lines
      doc.text(`${companyInfo.name}`, pageWidth / 2, currentY, { align: 'center' });
      currentY += 6;
      
      doc.text(`${companyInfo.address}`, pageWidth / 2, currentY, { align: 'center' });
      currentY += 6;
      
      // Split contact info to avoid overlap
      doc.text(`Phone: ${companyInfo.phone}`, pageWidth / 2, currentY, { align: 'center' });
      currentY += 6;
      
      doc.text(`Email: ${companyInfo.email}`, pageWidth / 2, currentY, { align: 'center' });
      currentY += 6;
      
      doc.text(`Contact: ${companyInfo.contact}`, pageWidth / 2, currentY, { align: 'center' });
      currentY += 12;
      
      // Report details
      doc.setFontSize(9);
      doc.setTextColor(80, 80, 80);
      doc.text(`Report Generated: ${new Date().toLocaleString()}`, margin, currentY);
      
      const totalRecordsText = `Total Records: ${inventory.length}`;
      const totalRecordsWidth = doc.getTextWidth(totalRecordsText);
      doc.text(totalRecordsText, pageWidth - margin - totalRecordsWidth, currentY);
      
      currentY += 10;
      
      // Apply filters text if any
      const filterTexts = [];
      if (filters.supplier_id) {
        filterTexts.push(`Supplier: ${getSupplierName(filters.supplier_id, supplierNamesData)}`);
      }
      if (filters.dateFrom) {
        filterTexts.push(`From: ${new Date(filters.dateFrom).toLocaleDateString()}`);
      }
      if (filters.dateTo) {
        filterTexts.push(`To: ${new Date(filters.dateTo).toLocaleDateString()}`);
      }
      
      if (filterTexts.length > 0) {
        doc.setFontSize(8);
        const filtersText = `Filters: ${filterTexts.join(', ')}`;
        const filtersLines = splitTextIntoLines(doc, filtersText, pageWidth - 2 * margin);
        
        filtersLines.forEach(line => {
          if (currentY > pageHeight - 40) {
            doc.addPage('l');
            currentY = margin;
          }
          doc.text(line, margin, currentY);
          currentY += 5;
        });
        
        currentY += 10;
      }
      
      // Prepare table data with required columns including Branch
      const tableData = inventory.map((item, index) => [
        (index + 1).toString(),
        new Date(item.stock_date).toLocaleDateString(),
        getSupplierName(item.supplier_id, supplierNamesData),
        getStockName(item.stock_id, stockNamesData),
        formatCurrency(item.quantity),
        formatCurrency(item.cost),
        formatCurrency(item.selling),
        getBranchName(item.branch_id, branchNamesFilterData)
      ]);
      
      // Draw the table
      drawInventoryTable(doc, tableData, currentY + 10, referenceData);
      
      // Add page numbers
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
      }
      
      const fileName = `restaurant_inventory_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      resolve({ success: true, fileName });
    } catch (error) {
      console.error('Restaurant Inventory PDF export failed:', error);
      reject(new Error('Failed to export to PDF: ' + error.message));
    }
  });
};

// CSV export for Restaurant Inventory
export const exportToCSV = (inventory, referenceData = {}) => {
  try {
    const { supplierNamesData = [], stockNamesData = [], branchNamesFilterData = [] } = referenceData;
    
    // CSV with ALL attributes including Branch
    const headers = ['No', 'ID', 'Stock Date', 'Supplier', 'Stock Item', 'Quantity', 'Cost Price', 'Selling Price', 'Total Cost', 'Total Selling', 'Profit Margin', 'Description', 'Branch', 'Created Date', 'Updated Date'];
    
    const csvData = inventory.map((item, index) => [
      index + 1,
      item.id,
      new Date(item.stock_date).toISOString().split('T')[0],
      `"${getSupplierName(item.supplier_id, supplierNamesData).replace(/"/g, '""')}"`,
      `"${getStockName(item.stock_id, stockNamesData).replace(/"/g, '""')}"`,
      item.quantity,
      item.cost,
      item.selling,
      (item.quantity * item.cost).toFixed(2),
      (item.quantity * item.selling).toFixed(2),
      ((item.selling - item.cost) * item.quantity).toFixed(2),
      `"${(item.description || '').replace(/"/g, '""')}"`,
      `"${getBranchName(item.branch_id, branchNamesFilterData).replace(/"/g, '""')}"`,
      new Date(item.created_at).toISOString().split('T')[0],
      new Date(item.updated_at).toISOString().split('T')[0]
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `restaurant_inventory_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return { success: true };
  } catch (error) {
    console.error('Restaurant Inventory CSV export failed:', error);
    throw new Error('Failed to export to CSV: ' + error.message);
  }
};